import React, { useEffect, useState } from "react";
import { Box, Alert } from "@mui/material";
import { useParams } from "react-router-dom";
import MeetingWithWhiteboard from "../../components/MeetingWithWhiteboard";
import api from "../../api";

/**
 * Tutor meeting tab: self-hosted Jitsi + shared Excalidraw whiteboard.
 * Moderator gets Cloudflare Stream Live auto-record when Stream is configured.
 */
export default function TutorMeetingPage() {
  const { roomCode } = useParams();
  const tutorName = localStorage.getItem("tutor_name") || "Tutor";
  const code = roomCode || "";

  const [drawingEnabled, setDrawingEnabled] = useState(true);
  const [jitsi, setJitsi] = useState(null);
  const [joinError, setJoinError] = useState(null);
  const [loading, setLoading] = useState(true);

  const isTeacher = (localStorage.getItem("role") || "").toLowerCase() === "tutor";

  useEffect(() => {
    if (!code) {
      setLoading(false);
      setJoinError("Missing room code.");
      return;
    }
    let cancelled = false;
    setJoinError(null);
    setLoading(true);
    api
      .get(`tutor/classrooms/room/${encodeURIComponent(code)}/jitsi`)
      .then((res) => {
        if (cancelled) return;
        const enabled = res.data?.classroom?.drawing_enabled;
        if (typeof enabled === "boolean") setDrawingEnabled(enabled);
        const payload = res.data?.jitsi;
        if (payload) {
          const live = payload.live_recording || null;
          const mode =
            payload.recording_mode === "stream"
              ? "stream"
              : payload.recording_mode === "file"
                ? "file"
                : "local";
          setJitsi({
            displayName: payload.display_name || tutorName,
            isModerator: Boolean(payload.is_moderator),
            canRecord: Boolean(payload.can_record),
            autoStartRecording: Boolean(payload.auto_start_recording && payload.is_moderator),
            recordingMode: mode,
            rtmpDestination: live?.rtmp_destination || null,
            recordingId: live?.recording_id || null,
            jwt: payload.jwt || null,
          });
        } else {
          setJitsi({
            displayName: tutorName,
            isModerator: false,
            canRecord: false,
            autoStartRecording: false,
            recordingMode: "local",
            rtmpDestination: null,
            recordingId: null,
            jwt: null,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setJoinError("Could not load meeting permissions. Recording may be unavailable.");
        setJitsi({
          displayName: tutorName,
          isModerator: false,
          canRecord: false,
          autoStartRecording: false,
          recordingMode: "local",
          rtmpDestination: null,
          recordingId: null,
          jwt: null,
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, tutorName]);

  if (!code) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing room code.</Alert>
      </Box>
    );
  }

  return (
    <MeetingWithWhiteboard
      roomCode={code}
      displayName={jitsi?.displayName || tutorName}
      canRecord={Boolean(jitsi?.canRecord)}
      autoStartRecording={Boolean(jitsi?.autoStartRecording)}
      recordingMode={jitsi?.recordingMode || "local"}
      rtmpDestination={jitsi?.rtmpDestination || null}
      recordingId={jitsi?.recordingId || null}
      jwt={jitsi?.jwt || null}
      isModerator={Boolean(jitsi?.isModerator)}
      drawingEnabled={drawingEnabled}
      isTeacher={isTeacher}
      joinError={joinError}
      loading={loading || !jitsi}
      leavePath="/tutor/classes"
      meetingLabel="Meeting"
    />
  );
}
